import {createSelector} from '@reduxjs/toolkit'
import {QueryStatus, skipToken} from '@reduxjs/toolkit/query'
import getRoomConfig from 'components/data/URLParams/mappers/getRoomConfig'
import {getExistingBookingQueryParams} from 'modules/book/selectors'
import {getOfferCheckOverrideUrl} from 'modules/common/selectors'
import {
  getAnonymousId,
  getPiccCardCountry,
  getSearchId,
  getUrlParams,
  getUserSelectedCurrency
} from 'modules/meta/selectors'
import {RootState} from 'store'
import {urlParams} from 'utils/urlParams'

import {ErrorContext} from '@vio/core/src/analytics/types/Events'
import {PriceType} from '@vio/core/src/api-types/bovio/response/offer_check'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {ErrorResponse} from '@vio/core/src/booking/types/ErrorResponse'
import {
  getBovioApiKey,
  getSapiVariationsHeader
} from '@vio/core/src/experiments/modules/selectors'
import {getCancellationDeadline} from '@vio/core/src/offer/business/cancellationPenalties'
import {getOfferCheckApiRequestParams} from '@vio/core/src/offer/business/getOfferCheckApiRequestParams'
import {
  getOfferPaymentMode,
  hasChargeLaterPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {getPriceDetails} from '@vio/core/src/price/business/price'
import {getEpochSeconds} from '@vio/core/src/utils/date'
import {logger} from '@vio/core/src/utils/logging/logger'
import {mapBedroomsToUniqueBedConfigurationOptions} from '@vio/shared/src/accommodation/RoomInfo/utils'

import {getCardCurrency} from './cardCurrency'
import {CARD_CURRENCY_PARAM} from './contants'
import {PaymentFlowType, PiccErrorType} from './types'
import {
  buildOfferCheckEndpointFromCurrentUrl,
  existingBookingOfferCheckDataTransform,
  offerCheckTransformResponseFunc,
  RECHECK_METADATA_ID_URL_PARAM,
  withOfferCheckToken
} from './utils'

const getStateParam = (state: RootState) => state

export const getPaymentFlowType = createSelector(
  [getUrlParams],
  ({bookingId, offer_token, resumeFlow, bundleId}) => {
    const isResumeFlow = resumeFlow === 'true'

    if (bookingId && isResumeFlow) return PaymentFlowType.ResumeBooking
    if (bookingId) return PaymentFlowType.ExistingBooking
    if (bundleId) return PaymentFlowType.SplitBooking
    if (offer_token) return PaymentFlowType.RegularOffer
  }
)

export const getOfferCheckEndpoint = createSelector(
  [getAnonymousId, getSearchId],
  buildOfferCheckEndpointFromCurrentUrl
)

export const buildOfferCheckApiRequestParams = createSelector(
  [
    getSapiVariationsHeader,
    getOfferCheckOverrideUrl,
    getOfferCheckEndpoint,
    getBovioApiKey
  ],
  (sapiVariationsHeader, overrideUrl, offerCheckUrl, apiKey) => {
    // Check if we have an override URL from Redux first
    if (overrideUrl) {
      return getOfferCheckApiRequestParams(
        overrideUrl,
        apiKey,
        sapiVariationsHeader
      )
    }

    // Otherwise, build the URL and params as usual
    const currentUrlParams = urlParams()

    const {apRequestId} = currentUrlParams

    // if apRequestId is present, we are using the cache
    const cacheSupported = Boolean(apRequestId)

    const headers = {
      ...sapiVariationsHeader,
      ...(cacheSupported ? {'X-Offer-Check-Cache-Support': 'true'} : {}),
      ...(apRequestId ? {'X-Request-Id': apRequestId} : {})
    }

    return getOfferCheckApiRequestParams(offerCheckUrl, apiKey, headers)
  }
)

const getMainOfferCheckSelector = createSelector(
  [buildOfferCheckApiRequestParams],
  params => offerCheckApi.endpoints.getOfferCheck.select(params)
)

// don't use directly unless strictly necessary, use getRegularOfferCheck instead
export const getMainOfferCheck = createSelector(
  [getStateParam, getMainOfferCheckSelector],
  (state: RootState, mainOfferCheckSelector) => mainOfferCheckSelector(state)
)

/**
 * [PICC] The token from the main offer check -> reused for secondary PICC offer check
 */
const getMainOfferCheckToken = createSelector(
  getMainOfferCheck,
  ({data}) => data?.token
)

/**
 * [PICC] Re-check anchor BoVio needs to verify the main check's token
 */
const getMainOfferCheckRecheckMetadataId = createSelector(
  getMainOfferCheck,
  ({data}) => data?.recheckMetadataId
)

export const getPayInCardCurrencyOfferCheckParams = createSelector(
  [
    buildOfferCheckApiRequestParams,
    getPiccCardCountry,
    getMainOfferCheckToken,
    getMainOfferCheckRecheckMetadataId
  ],
  (baseParams, cardCountry, mainOfferCheckToken, recheckMetadataId) => {
    // Return early if URL is invalid or empty
    if (!baseParams.url) {
      return baseParams
    }

    try {
      const url = new URL(
        mainOfferCheckToken
          ? withOfferCheckToken(baseParams.url, mainOfferCheckToken)
          : baseParams.url
      )

      if (mainOfferCheckToken && recheckMetadataId) {
        url.searchParams.set(RECHECK_METADATA_ID_URL_PARAM, recheckMetadataId)
      }

      const cardCurrency = cardCountry
        ? getCardCurrency(cardCountry)
        : undefined
      if (cardCurrency) {
        url.searchParams.set(CARD_CURRENCY_PARAM, cardCurrency)
      }

      return {
        ...baseParams,
        url: url.toString()
      }
    } catch (error) {
      // If URL construction fails, return baseParams as-is
      logger.error('Failed to construct URL for PICC params:', error)
      return baseParams
    }
  }
)

const getPayInCardCurrencyOfferCheckSelector = createSelector(
  [getPayInCardCurrencyOfferCheckParams],
  params => {
    const hasCardCurrencyVariant = params.url?.includes(
      `${CARD_CURRENCY_PARAM}=`
    )
    return offerCheckApi.endpoints.getOfferCheck.select(
      hasCardCurrencyVariant ? params : skipToken
    )
  }
)

// don't use directly unless strictly necessary, use getRegularOfferCheck instead
export const getPayInCardCurrencyOfferCheck = createSelector(
  [getStateParam, getPayInCardCurrencyOfferCheckSelector],
  (state: RootState, payInCardCurrencyOfferCheckSelector) =>
    payInCardCurrencyOfferCheckSelector(state)
)
export const getIsPiccOfferCheckSuccess = createSelector(
  getPayInCardCurrencyOfferCheck,
  piccOfferCheck => piccOfferCheck?.isSuccess ?? false
)

export const getIsPiccOfferCheckLoading = createSelector(
  getPayInCardCurrencyOfferCheck,
  piccOfferCheck => piccOfferCheck?.status === QueryStatus.pending
)

/**
 * [PICC] Currency choice info, retrieved from the card-currency offer check
 */
export const getPayInCardCurrencyInfo = createSelector(
  getPayInCardCurrencyOfferCheck,
  ({data}) => data?.payInCardCurrency
)

/**
 * [PICC] The chargeable currency, i.e. the `fromCurrency` a PICC conversion
 * starts from.
 */
export const getPiccFromCurrency = createSelector(
  getMainOfferCheck,
  ({data}) => data?.payInCardCurrency?.fromCurrency
)

/**
 * [PICC] Whether the display currency is also the chargeable currency. A
 * `display_price` entry means it is not: the backend converted, and it rejects
 * PICC for those offers.
 */
export const getIsDisplayCurrencyChargeable = createSelector(
  getMainOfferCheck,
  ({data}) =>
    data
      ? !data.prices.some(({type}) => type === PriceType.DisplayPrice)
      : false
)

/**
 * [PICC] The two currency options rendered in the card form
 */
export const getPiccCurrencyOptions = createSelector(
  [getMainOfferCheck, getPayInCardCurrencyOfferCheck],
  ({data: mainData}, {data: cardCurrencyData}) => {
    if (!mainData || !cardCurrencyData) return undefined

    const chargeable = getPriceDetails(
      offerCheckTransformResponseFunc(mainData).prices,
      false
    )
    const cardCurrency = getPriceDetails(
      offerCheckTransformResponseFunc(cardCurrencyData).prices,
      false
    )

    if (!chargeable.displayCurrencyCode || !cardCurrency.displayCurrencyCode) {
      return undefined
    }

    return {
      chargeable: {
        currency: chargeable.displayCurrencyCode,
        amount: chargeable.totalUpFrontDisplayPrice
      },
      cardCurrency: {
        currency: cardCurrency.displayCurrencyCode,
        amount: cardCurrency.totalUpFrontDisplayPrice
      }
    }
  }
)

export const getIsPiccAvailable = createSelector(
  getPayInCardCurrencyInfo,
  piccInfo =>
    Boolean(
      piccInfo?.eligible &&
        piccInfo?.toCurrency &&
        piccInfo?.rate &&
        piccInfo?.toCurrency !== piccInfo?.fromCurrency
    )
)

export const getPiccErrorContext = createSelector(
  getPayInCardCurrencyOfferCheck,
  ({isError, error, data}): ErrorContext | undefined => {
    if (isError) {
      // Network failures and non-JSON bodies come back as a plain string
      const {status, data: response} = (error || {}) as {
        status?: number
        data?: ErrorResponse | string
      }
      const isStringResponse = typeof response === 'string'

      return {
        errorType: PiccErrorType.OfferCheckFailed,
        errorCode: status === undefined ? undefined : String(status),
        errorMessage: isStringResponse ? response : response?.message,
        errorDetails: isStringResponse ? undefined : {...response}
      }
    }

    if (!data) return undefined

    if (!data.payInCardCurrency?.eligible) {
      return {
        errorType: PiccErrorType.Ineligible,
        errorDetails: {...data.payInCardCurrency}
      }
    }

    return undefined
  }
)

export const getIsCardCurrencySelected = createSelector(
  [getUserSelectedCurrency, getPayInCardCurrencyInfo, getIsPiccAvailable],
  (userSelectedCurrency, piccInfo, isPiccAvailable) =>
    Boolean(
      isPiccAvailable &&
        userSelectedCurrency &&
        userSelectedCurrency === piccInfo?.toCurrency
    )
)

export const getRegularOfferCheck = createSelector(
  [
    getIsCardCurrencySelected,
    getMainOfferCheck,
    getPayInCardCurrencyOfferCheck
  ],
  (isCardCurrencySelected, mainOfferCheck, payInCardCurrencyOfferCheck) => {
    const result = isCardCurrencySelected
      ? payInCardCurrencyOfferCheck
      : mainOfferCheck

    return {
      ...result,
      data: result.data
        ? offerCheckTransformResponseFunc(result.data)
        : undefined
    }
  }
)

const getExistingBookingOfferCheckSelector = createSelector(
  [getExistingBookingQueryParams],
  params => bookingRetrieveApi.endpoints.getBookingById.select(params)
)

const getExistingBookingOfferCheckResult = createSelector(
  [getStateParam, getExistingBookingOfferCheckSelector],
  (state, existingBookingOfferCheckSelector) =>
    existingBookingOfferCheckSelector(state)
)
export const getExistingBookingOfferCheck = createSelector(
  [getExistingBookingOfferCheckResult],
  result => ({
    ...result,
    data: result.data
      ? existingBookingOfferCheckDataTransform(result.data)
      : undefined
  })
)

//returns unified offerCheck data wrapper for offer-check sb-offer-check and CL/DF booking data
export const getUnifiedOfferCheck = createSelector(
  [getPaymentFlowType, getExistingBookingOfferCheck, getRegularOfferCheck],
  (type, existingBookingOfferCheck, regularOfferCheck) => {
    switch (type) {
      case PaymentFlowType.ExistingBooking:
        return existingBookingOfferCheck
      default:
        return regularOfferCheck
    }
  }
)

// don't use directly unless strictly necessary, use getUnifiedOfferCheck instead
export const getMainUnifiedOfferCheck = createSelector(
  [getPaymentFlowType, getExistingBookingOfferCheck, getMainOfferCheck],
  (type, existingBookingOfferCheck, mainOfferCheck) => {
    if (type === PaymentFlowType.ExistingBooking) {
      return existingBookingOfferCheck
    }

    return {
      ...mainOfferCheck,
      data: mainOfferCheck.data
        ? offerCheckTransformResponseFunc(mainOfferCheck.data)
        : undefined
    }
  }
)

export const getIsOfferCheckExistingBookingValid = createSelector(
  getUnifiedOfferCheck,
  ({data}) => Boolean(data?.bookingInfo && data.bookingInfo.confirmationId)
)

export const getUniqueBedConfigurationOptions = createSelector(
  getRegularOfferCheck, // only for incoming bookings (skip existing)
  ({data}) =>
    data?.rooms?.[0]?.bedrooms?.length
      ? mapBedroomsToUniqueBedConfigurationOptions(data?.rooms?.[0]?.bedrooms)
      : []
)

// TODO: [sc-144153] @BCExp - This selector may be redundant and should be reviewed for removal later.
export const getOfferCheck = createSelector(
  getRegularOfferCheck,
  ({data}) => data
)

export const getRoomNames = createSelector(
  getUnifiedOfferCheck,
  ({data: offerData}) => {
    const roomsConfig = getRoomConfig(urlParams())

    return new Array(roomsConfig.numberOfRooms).fill(
      offerData?.rooms?.[0]?.name,
      0,
      roomsConfig.numberOfRooms
    )
  }
)

export const getRoomGuestName = createSelector(getUnifiedOfferCheck, ({data}) =>
  data?.rooms?.map(room => {
    return {firstName: room.firstName, lastName: room.lastName}
  })
)

export const getEpochCancellationDeadline = createSelector(
  getOfferCheck,
  offer => {
    const cancellationDeadline = getCancellationDeadline(
      offer?.cancellationPenalties ?? []
    )
    return cancellationDeadline ? getEpochSeconds(cancellationDeadline) : null
  }
)

export const getOfferCheckError = createSelector(
  getUnifiedOfferCheck,
  offerCheck => offerCheck.error
)

export const getOfferCheckLoadingState = createSelector(
  getUnifiedOfferCheck,
  offerCheck => offerCheck.isSuccess
)

export const getOfferCheckMerchantOfRecord = createSelector(
  getUnifiedOfferCheck,
  ({data}) => data?.merchantOfRecord
)

export const getCheckoutPaymentMode = createSelector(
  [getUnifiedOfferCheck, getPaymentFlowType],
  ({data}, flowType) =>
    getOfferPaymentMode({
      paymentMode: data?.paymentMode,
      isSplitBooking: flowType === PaymentFlowType.SplitBooking
    })
)

export const getIsOfferCheckRefetching = createSelector(
  [getMainOfferCheck],
  ({data, status}) => {
    // if data is present, but status is pending it means it's in refetching mode
    // this is to emulate isRefetch on selector level (only available in hooks)
    return Boolean(data) && status === QueryStatus.pending
  }
)
export const getIsChargeLaterEligible = createSelector(
  [getUnifiedOfferCheck],
  ({data}) => {
    return hasChargeLaterPayment(data)
  }
)
